import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, Banknote, ArrowLeftRight, MoreHorizontal, Users, Baby, User, Crown, PiggyBank, Calendar, SplitSquareHorizontal, RefreshCw, Plus, Trash2, UtensilsCrossed, Plane, Gift, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { createTransaction, getFamily, createSubscription } from '../lib/api';
import { getCategoryColor } from '../lib/utils';
import { toast } from 'sonner';

const CATEGORIES = [
  { id: 'alimentation', emoji: '🍔', label_fr: 'Alimentation', label_en: 'Food', label_pt: 'Alimentação' },
  { id: 'restaurant', emoji: '🍽️', label_fr: 'Restaurant', label_en: 'Restaurant', label_pt: 'Restaurante' },
  { id: 'transport', emoji: '🚗', label_fr: 'Transport', label_en: 'Transport', label_pt: 'Transporte' },
  { id: 'shopping', emoji: '🛍️', label_fr: 'Shopping', label_en: 'Shopping', label_pt: 'Compras' },
  { id: 'loisirs', emoji: '🎮', label_fr: 'Loisirs', label_en: 'Entertainment', label_pt: 'Lazer' },
  { id: 'logement', emoji: '🏠', label_fr: 'Logement', label_en: 'Housing', label_pt: 'Habitação' },
  { id: 'abonnements', emoji: '📱', label_fr: 'Abonnements', label_en: 'Subscriptions', label_pt: 'Assinaturas' },
  { id: 'sante', emoji: '💊', label_fr: 'Santé', label_en: 'Health', label_pt: 'Saúde' },
  { id: 'epargne', emoji: '💰', label_fr: 'Épargne', label_en: 'Savings', label_pt: 'Poupança' },
  { id: 'education', emoji: '🎓', label_fr: 'Éducation', label_en: 'Education', label_pt: 'Educação' },
  { id: 'cadeaux', emoji: '🎁', label_fr: 'Cadeaux', label_en: 'Gifts', label_pt: 'Presentes' },
  { id: 'divers', emoji: '📦', label_fr: 'Divers', label_en: 'Other', label_pt: 'Outros' },
];

const PAYMENT_METHODS = [
  { id: 'card', icon: CreditCard, label_fr: 'Carte', label_en: 'Card', label_pt: 'Cartão', emoji: '💳' },
  { id: 'cash', icon: Banknote, label_fr: 'Espèces', label_en: 'Cash', label_pt: 'Dinheiro', emoji: '💵' },
  { id: 'transfer', icon: ArrowLeftRight, label_fr: 'Virement', label_en: 'Transfer', label_pt: 'Transferência', emoji: '🏦' },
  { id: 'check', icon: FileText, label_fr: 'Chèque', label_en: 'Check', label_pt: 'Cheque', emoji: '📝' },
  { id: 'ticket_restaurant', icon: UtensilsCrossed, label_fr: 'Ticket resto', label_en: 'Meal voucher', label_pt: 'Vale refeição', emoji: '🍽️' },
  { id: 'cheque_vacances', icon: Plane, label_fr: 'Chèque vacances', label_en: 'Holiday voucher', label_pt: 'Vale férias', emoji: '🏖️' },
  { id: 'cheque_cadeau', icon: Gift, label_fr: 'Chèque cadeau', label_en: 'Gift card', label_pt: 'Vale presente', emoji: '🎁' },
  { id: 'other', icon: MoreHorizontal, label_fr: 'Autre', label_en: 'Other', label_pt: 'Outro', emoji: '📋' },
];

const INSTALLMENT_OPTIONS = [2, 3, 4, 6, 10, 12];

export default function QuickAddModal({ open, onClose, onSuccess, defaultType }) {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  
  // Check if user is a child
  const isChild = user?.family_role === 'child';
  
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('alimentation');
  const [description, setDescription] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [forMemberId, setForMemberId] = useState('');
  const [attributedMemberIds, setAttributedMemberIds] = useState([]); // Multi-member attribution
  const [budgetType, setBudgetType] = useState('personal');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [xpGained, setXpGained] = useState(5);
  const [family, setFamily] = useState(null);
  
  // Multiple payment methods state
  const [useMultiplePayments, setUseMultiplePayments] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  
  // Installment payment state
  const [isInstallment, setIsInstallment] = useState(false);
  const [totalInstallments, setTotalInstallments] = useState(3);
  
  // Subscription state
  const [isSubscription, setIsSubscription] = useState(false);
  const [subscriptionDay, setSubscriptionDay] = useState(1);
  
  // Date of expense (defaults to today)
  const [expenseDate, setExpenseDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // YYYY-MM-DD
  });

  // Load family data when modal opens
  useEffect(() => {
    if (open && user?.family_id) {
      loadFamily();
    }
  }, [open, user?.family_id]);

  const loadFamily = async () => {
    try {
      const res = await getFamily();
      setFamily(res.data.family);
    } catch (error) {
      console.error('Failed to load family:', error);
    }
  };

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) return;
    if (isSubscription && !description) {
      // Subscription requires a name/description
      return;
    }
    
    // Validate multiple payments if enabled
    if (useMultiplePayments && paymentMethods.length > 0) {
      const totalPayment = paymentMethods.reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
      if (Math.abs(totalPayment - parseFloat(amount)) > 0.01) {
        alert(language === 'fr' 
          ? `La somme des paiements (${totalPayment.toFixed(2)}€) ne correspond pas au montant total (${parseFloat(amount).toFixed(2)}€)`
          : language === 'pt'
          ? `O total dos pagamentos (${totalPayment.toFixed(2)}€) não corresponde ao montante (${parseFloat(amount).toFixed(2)}€)`
          : `Payment total (€${totalPayment.toFixed(2)}) doesn't match expense amount (€${parseFloat(amount).toFixed(2)})`
        );
        return;
      }
    }
    
    setIsSubmitting(true);
    try {
      // If subscription, create subscription instead of transaction
      if (isSubscription) {
        await createSubscription({
          name: description,
          amount: parseFloat(amount),
          category,
          payment_day: subscriptionDay,
          budget_type: family?.budget_mode === 'hybrid' ? budgetType : 'personal',
        });
        
        setSuccessMessage(
          language === 'fr' 
            ? `Abonnement "${description}" créé ! Prélèvement le ${subscriptionDay} de chaque mois.`
            : language === 'pt'
            ? `Assinatura "${description}" criada! Cobrada no dia ${subscriptionDay} de cada mês.`
            : `Subscription "${description}" created! Charged on the ${subscriptionDay}th of each month.`
        );
        setXpGained(10);
      } else {
        // Build payment_methods array
        const finalPaymentMethods = useMultiplePayments && paymentMethods.length > 0
          ? paymentMethods.map(pm => ({ type: pm.type, amount: parseFloat(pm.amount) }))
          : [];
        
        const response = await createTransaction({
          amount: parseFloat(amount),
          category,
          description: description || undefined,
          payment_method: paymentMethod,
          payment_methods: finalPaymentMethods,
          for_member_id: forMemberId || undefined,
          attributed_member_ids: attributedMemberIds.length > 0 ? attributedMemberIds : undefined,
          budget_type: family?.budget_mode === 'hybrid' ? budgetType : 'personal',
          is_installment: isInstallment,
          total_installments: isInstallment ? totalInstallments : undefined,
          date: expenseDate ? new Date(expenseDate + 'T12:00:00').toISOString() : undefined,
        });
        
        // Check for transaction limit warning
        if (response.data.warning) {
          const warning = response.data.warning;
          const message = language === 'fr' ? warning.message_fr : language === 'pt' ? (warning.message_pt || warning.message_en) : warning.message_en;
          
          if (warning.type === 'transaction_limit_reached') {
            toast.error(message, { duration: 6000 });
          } else if (warning.type === 'transaction_limit_approaching') {
            toast.warning(message, { duration: 5000 });
          }
        }
        
        // Set success message based on response
        if (response.data.installment_group_id) {
          const installmentAmount = response.data.installment_amount;
          setSuccessMessage(
            language === 'fr' 
              ? `Paiement en ${totalInstallments}x créé ! ${totalInstallments} mensualités de ${installmentAmount.toFixed(2)}€`
              : language === 'pt'
              ? `Pagamento em ${totalInstallments}x criado! ${totalInstallments} prestações de ${installmentAmount.toFixed(2)}€`
              : `${totalInstallments}x payment created! ${totalInstallments} installments of €${installmentAmount.toFixed(2)}`
          );
          setXpGained(response.data.xp_gained || 10);
        } else if (useMultiplePayments && paymentMethods.length > 0) {
          setSuccessMessage(
            language === 'fr' 
              ? `Dépense ajoutée avec ${paymentMethods.length} moyens de paiement !`
              : language === 'pt'
              ? `Despesa adicionada com ${paymentMethods.length} métodos de pagamento!`
              : `Expense added with ${paymentMethods.length} payment methods!`
          );
          setXpGained(response.data.xp_gained || 5);
        } else {
          setSuccessMessage(t('expense_added'));
          setXpGained(response.data.xp_gained || 5);
        }
      }
      
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        resetForm();
        onSuccess?.();
      }, 1500);
    } catch (error) {
      console.error('Failed to create transaction:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setAmount('');
    setCategory('alimentation');
    setDescription('');
    setPaymentMethod('card');
    setForMemberId('');
    setAttributedMemberIds([]);
    setBudgetType('personal');
    setUseMultiplePayments(false);
    setPaymentMethods([]);
    setIsInstallment(false);
    setTotalInstallments(3);
    setIsSubscription(false);
    setSubscriptionDay(1);
    setExpenseDate(new Date().toISOString().split('T')[0]);
    setSuccessMessage('');
    setXpGained(5);
  };

  // Helper functions for multiple payment methods
  const addPaymentMethod = () => {
    const remainingAmount = parseFloat(amount || 0) - paymentMethods.reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
    setPaymentMethods([...paymentMethods, { type: 'card', amount: Math.max(0, remainingAmount).toFixed(2) }]);
  };
  
  const updatePaymentMethod = (index, field, value) => {
    const updated = [...paymentMethods];
    updated[index] = { ...updated[index], [field]: value };
    setPaymentMethods(updated);
  };
  
  const removePaymentMethod = (index) => {
    setPaymentMethods(paymentMethods.filter((_, i) => i !== index));
  };
  
  const getRemainingAmount = () => {
    const total = parseFloat(amount || 0);
    const allocated = paymentMethods.reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
    return (total - allocated).toFixed(2);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const quickAmounts = [10, 20, 50, 100];
  
  // Calculate installment amount preview
  const installmentAmount = isInstallment && amount 
    ? (parseFloat(amount) / totalInstallments).toFixed(2) 
    : null;

  // Compute date warning
  const getDateWarning = () => {
    if (!expenseDate) return null;
    const selected = new Date(expenseDate + 'T12:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDay = new Date(selected);
    selectedDay.setHours(0, 0, 0, 0);
    
    const sameMonth = selected.getFullYear() === today.getFullYear() && selected.getMonth() === today.getMonth();
    
    if (selectedDay > today) {
      return { type: 'future', message: t('date_warning_future') };
    }
    if (!sameMonth && selectedDay < today) {
      // Past month
      const monthsAgo = (today.getFullYear() - selected.getFullYear()) * 12 + (today.getMonth() - selected.getMonth());
      if (monthsAgo > 6) {
        return { type: 'far_past', message: t('date_warning_far_past') };
      }
      return { type: 'past_month', message: t('date_warning_past_month') };
    }
    return null;
  };
  const dateWarning = getDateWarning();

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] max-w-md max-h-[90vh] overflow-y-auto p-4">
        <DialogHeader>
          <DialogTitle>{t('add_expense')}</DialogTitle>
        </DialogHeader>
        
        <AnimatePresence mode="wait">
          {showSuccess ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="py-12 text-center"
            >
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring' }}
                  className="text-3xl"
                >
                  ✓
                </motion.div>
              </div>
              <p className="text-lg font-medium">
                {successMessage}
              </p>
              <p className="text-sm text-primary mt-1">+{xpGained} XP</p>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-5 pt-4 min-w-0"
            >
              {/* Amount Input */}
              <div>
                <label className="text-sm font-medium mb-2 block">{t('amount')}</label>
                <div className="relative">
                  <Input
                    type="number"
                    inputMode="decimal"
                    placeholder="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="h-16 text-3xl font-bold text-center pr-12"
                    data-testid="quick-add-amount"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-2xl text-muted-foreground">€</span>
                </div>
                
                {/* Quick amount buttons */}
                <div className="flex gap-2 mt-3">
                  {quickAmounts.map((qa) => (
                    <Button
                      key={qa}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => setAmount(qa.toString())}
                      data-testid={`quick-amount-${qa}`}
                    >
                      {qa}€
                    </Button>
                  ))}
                </div>
              </div>

              {/* Date Picker */}
              {!isSubscription && (
                <div data-testid="expense-date-section">
                  <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    {t('expense_date')}
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="date"
                      value={expenseDate}
                      onChange={(e) => setExpenseDate(e.target.value)}
                      className="flex-1"
                      data-testid="expense-date-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setExpenseDate(new Date().toISOString().split('T')[0])}
                      data-testid="expense-date-today-btn"
                    >
                      {t('date_today')}
                    </Button>
                  </div>
                  {dateWarning && (
                    <motion.div
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-2 p-2 rounded-lg text-xs ${
                        dateWarning.type === 'past_month' || dateWarning.type === 'far_past'
                          ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800'
                          : 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-800'
                      }`}
                      data-testid={`expense-date-warning-${dateWarning.type}`}
                    >
                      {dateWarning.message}
                    </motion.div>
                  )}
                </div>
              )}

              {/* Installment Payment Toggle */}
              <div className={`bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20 rounded-xl p-4 ${isSubscription ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                      <SplitSquareHorizontal className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">
                        {t('installment_payment')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('split_over_months')}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={isInstallment}
                    onCheckedChange={(checked) => {
                      setIsInstallment(checked);
                      if (checked) setIsSubscription(false);
                    }}
                    disabled={isSubscription}
                    data-testid="installment-toggle"
                  />
                </div>
                
                {/* Installment options */}
                <AnimatePresence>
                  {isInstallment && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-4 space-y-3">
                        <p className="text-sm font-medium">
                          {t('number_of_installments')}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {INSTALLMENT_OPTIONS.map((num) => (
                            <button
                              key={num}
                              onClick={() => setTotalInstallments(num)}
                              className={`px-4 py-2 rounded-lg border-2 font-medium transition-all ${
                                totalInstallments === num
                                  ? 'border-purple-500 bg-purple-100 text-purple-700'
                                  : 'border-border hover:border-purple-300'
                              }`}
                              data-testid={`installment-${num}`}
                            >
                              {num}x
                            </button>
                          ))}
                        </div>
                        
                        {/* Preview */}
                        {installmentAmount && (
                          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border">
                            <p className="text-sm text-muted-foreground">
                              {t('preview')}
                            </p>
                            <p className="font-bold text-purple-600">
                              {totalInstallments} × {installmentAmount}€ 
                              <span className="text-muted-foreground font-normal text-sm ml-2">
                                ({t('per_month')})
                              </span>
                            </p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Subscription Toggle */}
              <div className={`border rounded-xl p-4 ${isSubscription ? 'border-green-300 bg-green-50 dark:bg-green-900/20' : 'border-border'} ${isInstallment ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <RefreshCw className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">
                        {t('recurring_subscription')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('auto_charged_monthly')}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={isSubscription}
                    onCheckedChange={(checked) => {
                      setIsSubscription(checked);
                      if (checked) {
                        setIsInstallment(false);
                        // Default to abonnements category for subscriptions
                        setCategory('abonnements');
                      }
                    }}
                    disabled={isInstallment}
                    data-testid="subscription-toggle"
                  />
                </div>
                
                {/* Subscription options */}
                <AnimatePresence>
                  {isSubscription && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-4 space-y-3">
                        <p className="text-sm font-medium">
                          {t('payment_day')}
                        </p>
                        <Select value={subscriptionDay.toString()} onValueChange={(v) => setSubscriptionDay(parseInt(v))}>
                          <SelectTrigger className="w-full" data-testid="subscription-day-select">
                            <SelectValue placeholder={t('select_day')} />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({length: 28}, (_, i) => i + 1).map((day) => (
                              <SelectItem key={day} value={day.toString()}>
                                {day === 1 ? t('first_day') : day}{t('day_of_month')}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        {/* Subscription preview */}
                        {amount && description && (
                          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-green-200">
                            <p className="text-sm text-muted-foreground">
                              {t('summary_label')}
                            </p>
                            <p className="font-bold text-green-600">
                              {description} - {parseFloat(amount).toFixed(2)}€/{t('month')}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {t('charged_on_day').replace('{day}', subscriptionDay)} 
                                : `Charged on the ${subscriptionDay}${subscriptionDay === 1 ? 'st' : subscriptionDay === 2 ? 'nd' : subscriptionDay === 3 ? 'rd' : 'th'} of each month`}
                            </p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Category Selection - Horizontal Slider */}
              <div className="min-w-0">
                <label className="text-sm font-medium mb-2 block">{t('category')}</label>
                <div 
                  className="flex gap-2 overflow-x-auto pb-2"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setCategory(cat.id)}
                      className={`flex-none w-14 p-2 rounded-xl border-2 transition-all ${
                        category === cat.id
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid={`category-${cat.id}`}
                    >
                      <span className="text-lg block text-center">{cat.emoji}</span>
                      <span className="text-[8px] text-center block mt-1 truncate">
                        {language === 'fr' ? cat.label_fr : language === 'pt' ? cat.label_pt : cat.label_en}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Payment Method */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">{t('payment_method')}</label>
                  <button
                    onClick={() => {
                      setUseMultiplePayments(!useMultiplePayments);
                      if (!useMultiplePayments && paymentMethods.length === 0) {
                        // Initialize with first payment method
                        setPaymentMethods([{ type: 'card', amount: amount || '0' }]);
                      }
                    }}
                    className={`text-xs px-2 py-1 rounded-full transition-colors ${
                      useMultiplePayments 
                        ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' 
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                    data-testid="toggle-multiple-payments"
                  >
                    {t('add_split_payment')}
                  </button>
                </div>
                
                {!useMultiplePayments ? (
                  // Horizontal scrollable payment method slider
                  <div 
                    className="flex gap-2 overflow-x-auto pb-2"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                  >
                    {PAYMENT_METHODS.map((method) => (
                      <button
                        key={method.id}
                        onClick={() => setPaymentMethod(method.id)}
                        className={`flex-none w-14 p-2 rounded-xl border-2 transition-all ${
                          paymentMethod === method.id
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`payment-${method.id}`}
                      >
                        <span className="text-lg block text-center">{method.emoji}</span>
                        <span className="text-[8px] block text-center mt-1 truncate">
                          {language === 'fr' ? method.label_fr : language === 'pt' ? method.label_pt : method.label_en}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  // Multiple payment methods UI
                  <div className="space-y-3 bg-cyan-50 dark:bg-cyan-950/20 rounded-xl p-3 border border-cyan-200 dark:border-cyan-800">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-cyan-700 dark:text-cyan-400">
                        {t('split_payment')}
                      </span>
                      <span className={`text-xs font-medium px-2 py-1 rounded ${
                        parseFloat(getRemainingAmount()) === 0 
                          ? 'bg-green-100 text-green-700' 
                          : parseFloat(getRemainingAmount()) < 0
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {parseFloat(getRemainingAmount()) === 0 
                          ? (t('complete'))
                          : (`${t('remaining_amount')}: ${getRemainingAmount()}€`)}
                      </span>
                    </div>
                    
                    {/* List of payment methods */}
                    {paymentMethods.map((pm, index) => (
                      <div key={index} className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded-lg p-2">
                        <Select 
                          value={pm.type} 
                          onValueChange={(v) => updatePaymentMethod(index, 'type', v)}
                        >
                          <SelectTrigger className="w-[140px]" data-testid={`payment-method-type-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PAYMENT_METHODS.map((method) => (
                              <SelectItem key={method.id} value={method.id}>
                                <span className="flex items-center gap-2">
                                  <span>{method.emoji}</span>
                                  <span>{language === 'fr' ? method.label_fr : language === 'pt' ? method.label_pt : method.label_en}</span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="relative flex-1">
                          <Input
                            type="number"
                            value={pm.amount}
                            onChange={(e) => updatePaymentMethod(index, 'amount', e.target.value)}
                            className="pr-8"
                            data-testid={`payment-method-amount-${index}`}
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                        </div>
                        <button
                          onClick={() => removePaymentMethod(index)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                          data-testid={`remove-payment-method-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    
                    {/* Add payment method button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addPaymentMethod}
                      className="w-full border-dashed border-cyan-300"
                      data-testid="add-payment-method"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {t('add_payment_method')}
                    </Button>
                  </div>
                )}
              </div>

              {/* Description (Required for subscription, Optional otherwise) */}
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {isSubscription 
                    ? (t('subscription_name'))
                    : t('description')
                  } 
                  {!isSubscription && (
                    <span className="text-muted-foreground">({t('optional')})</span>
                  )}
                  {isSubscription && <span className="text-red-500">*</span>}
                </label>
                <Input
                  placeholder={isSubscription 
                    ? (t('subscription_placeholder'))
                    : (t('example_grocery'))
                  }
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={isSubscription && !description ? 'border-red-300' : ''}
                  data-testid="quick-add-description"
                />
                {isSubscription && !description && (
                  <p className="text-xs text-red-500 mt-1">
                    {t('name_required_subscription')}
                  </p>
                )}
              </div>

              {/* Budget Type Selection (for hybrid mode) */}
              {family && family.budget_mode === 'hybrid' && (
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {t('budget_type')}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setBudgetType('personal')}
                      className={`p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                        budgetType === 'personal'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-border hover:border-blue-300'
                      }`}
                      data-testid="budget-type-personal"
                    >
                      <User className="w-4 h-4 text-blue-500" />
                      <span className="text-sm font-medium">{t('personal_budget')}</span>
                    </button>
                    <button
                      onClick={() => setBudgetType('common')}
                      className={`p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
                        budgetType === 'common'
                          ? 'border-green-500 bg-green-50'
                          : 'border-border hover:border-green-300'
                      }`}
                      data-testid="budget-type-common"
                    >
                      <PiggyBank className="w-4 h-4 text-green-500" />
                      <span className="text-sm font-medium">{t('common_budget')}</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Family Member Selection (if in a family AND not a child) */}
              {family && family.members && family.members.length > 1 && !isChild && (
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {t('for_label')} <span className="text-muted-foreground">({t('optional')})</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setForMemberId('')}
                      className={`px-3 py-2 rounded-lg border-2 transition-all flex items-center gap-2 ${
                        !forMemberId
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid="for-family-general"
                    >
                      <Users className="w-4 h-4" />
                      <span className="text-sm">{t('family')}</span>
                    </button>
                    {family.members
                      .filter(m => m.role === 'child')
                      .map((member) => (
                        <button
                          key={member.member_id}
                          onClick={() => setForMemberId(member.member_id)}
                          className={`px-3 py-2 rounded-lg border-2 transition-all flex items-center gap-2 ${
                            forMemberId === member.member_id
                              ? 'border-primary bg-primary/10'
                              : 'border-border hover:border-primary/50'
                          }`}
                          data-testid={`for-member-${member.member_id}`}
                        >
                          <Baby className="w-4 h-4 text-primary" />
                          <span className="text-sm">{member.name}</span>
                        </button>
                      ))}
                  </div>
                </div>
              )}

              {/* Multi-Member Attribution (shared/hybrid mode only) */}
              {family && ['shared', 'hybrid'].includes(family.budget_mode) && family.members && family.members.length > 1 && !isChild && !isSubscription && (
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {t('attributed_to_members')} <span className="text-muted-foreground">({t('optional')})</span>
                  </label>
                  <p className="text-xs text-muted-foreground mb-2">{t('attributed_to_members_hint')}</p>
                  <div className="flex flex-wrap gap-2" data-testid="attributed-members-picker">
                    {family.members.map((member) => {
                      const isSelected = attributedMemberIds.includes(member.member_id);
                      return (
                        <button
                          key={member.member_id}
                          type="button"
                          onClick={() => {
                            setAttributedMemberIds(prev =>
                              isSelected
                                ? prev.filter(id => id !== member.member_id)
                                : [...prev, member.member_id]
                            );
                          }}
                          className={`px-3 py-2 rounded-lg border-2 transition-all flex items-center gap-2 ${
                            isSelected
                              ? 'border-primary bg-primary/10'
                              : 'border-border hover:border-primary/50'
                          }`}
                          data-testid={`attrib-member-${member.member_id}`}
                        >
                          {member.role === 'child' ? <Baby className="w-4 h-4 text-primary" /> :
                           member.role === 'owner' ? <Crown className="w-4 h-4 text-amber-500" /> :
                           <User className="w-4 h-4" />}
                          <span className="text-sm">{member.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <Button
                onClick={handleSubmit}
                disabled={!amount || parseFloat(amount) <= 0 || isSubmitting}
                className="w-full h-12 text-lg font-bold"
                data-testid="quick-add-submit"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('adding')}
                  </span>
                ) : isInstallment ? (
                  t('create_installment_payment').replace('{count}', totalInstallments)
                ) : (
                  t('add_expense')
                )}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
